import { PureComponent, ReactNode, ReactElement } from "react";
import { Table, TableProps } from "antd";
import {
  SortableContainer as sortableContainer,
  SortableElement as sortableElement,
  SortableHandle,
  SortStart,
} from "react-sortable-hoc";
const randomString = (length: number): string => {
  const CHARS =
    "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";
  let result = "";
  for (let i = length; i > 0; --i)
    result += CHARS[Math.floor(Math.random() * CHARS.length)];
  return result;
};

const handleTableDragWidth = (
  params: SortStart,
  dragContainerId: string
): void => {
  const dContainer = document.getElementById(dragContainerId);
  const widths: number[] = [];
  if (dContainer?.children.length) {
    for (let j = 0; j < dContainer?.children.length; j++) {
      if (dContainer?.children[j]?.children?.length) {
        for (let i = 0; i < dContainer?.children[j]?.children?.length; i++) {
          widths.push(dContainer?.children[j]?.children[i].clientWidth);
        }
        break;
      }
    }
  }

  params.node.childNodes.forEach((_, index: number): void => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    params.helper.childNodes[index].style.width = widths[index] + "px";
  });
};

export const DragHandle = SortableHandle(() => (
  <span style={{ cursor: "move", color: "#999", fontSize: "22px" }}>☰</span>
));

export const SortableItem = sortableElement(
  (props: {
    children: ReactNode;
    className: string;
    "data-row-key": string;
  }): ReactElement => {
    return <tr {...props} />;
  }
);

export const SortableContainer = sortableContainer(
  (props: {
    children: ReactNode;
    className: string;
    id: string;
  }): ReactElement => {
    return <tbody {...props} id={props.id} />;
  }
);

type Item = {
  id: string;
};

interface Props<Record extends Item> extends TableProps<Record> {
  children?: ReactNode;
  onSortEnd?: ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => void;
  onSortStart?: (params: { index: number }) => void;
  findIndexForRecordKey: (id: string) => number;
  className: string;
}

class TableWithDragableRows<Record extends Item> extends PureComponent<
  Props<Record>
> {
  dragContainerId = randomString(4);

  onSortStart = (params: SortStart): void => {
    handleTableDragWidth(params, this.dragContainerId);

    this.props.onSortStart && this.props.onSortStart(params);
  };

  DraggableContainer = (props: {
    children: ReactNode;
    className: string;
  }): JSX.Element => (
    <SortableContainer
      useDragHandle
      onSortEnd={this.props.onSortEnd}
      onSortStart={this.onSortStart}
      helperContainer={() => {
        const container = document.getElementById(this.dragContainerId);
        if (container) {
          return container as HTMLElement;
        }
        // Fallback to modal body or document body
        const modalBody = document.querySelector(
          ".ant-modal-body"
        ) as HTMLElement;
        return modalBody || document.body;
      }}
      {...props}
      {...({ id: this.dragContainerId } as { id: string })}
    />
  );

  DraggableBodyRow = ({
    className,
    ...restProps
  }: {
    children: ReactNode;
    className: string;
    "data-row-key": string;
    style?: React.CSSProperties;
  }): JSX.Element => {
    const { findIndexForRecordKey: findIndexForRecordWithId } = this.props;
    const index = findIndexForRecordWithId(restProps["data-row-key"]);
    if (index === -1) {
      return <tr {...restProps} className={className} />;
    }
    return (
      <SortableItem
        index={index}
        {...restProps}
        {...({ className } as { className: string })}
      />
    );
  };

  components = {
    body: {
      wrapper: this.DraggableContainer,
      row: this.DraggableBodyRow,
    },
  };

  render(): ReactNode {
    const { children, className, rowClassName, ...restProps } = this.props;
    return (
      <Table
        tableLayout="fixed"
        components={this.components}
        className={`${className} antd-table-dashboard`}
        rowClassName={rowClassName || (() => "editable-row")}
        bordered
        rowKey={(record) => record.id}
        {...restProps}
      >
        {children}
      </Table>
    );
  }
}

export default TableWithDragableRows;
